import React, { createContext, useState } from 'react';
import { DropResult } from 'react-beautiful-dnd';

// Create a context with a more descriptive type
export interface DraggingContextType {
  isDragging: boolean;
  isDraggable: boolean;
  toggleDraggable: () => void;
  handleDragStart: () => void;
  handleDragEnd: (result: DropResult) => void;
}

// Create the context with a default value
export const DraggingContext = createContext<DraggingContextType | null>(null);

interface DraggingProviderProps {
  children: React.ReactNode | ((context: DraggingContextType) => React.ReactElement);
  onDragEnd: (result: DropResult) => void;
}

/**
 * Provider component for the dragging context
 * Manages the dragging state and provides handlers for drag events
 */
export const DraggingProvider: React.FC<DraggingProviderProps> = ({ children, onDragEnd }) => {
  // State to track if any item is being dragged
  const [isDragging, setIsDragging] = useState(false);
  // State to track if dragging is enabled
  const [isDraggable, setIsDraggable] = useState(false);

  // Toggle draggable state
  const toggleDraggable = () => {
    setIsDraggable((prevState) => !prevState);
  };

  // Handle drag start - set dragging state to true
  const handleDragStart = () => {
    setIsDragging(true);
  };

  // Handle drag end - reset dragging state and call the callback
  const handleDragEnd = (result: DropResult) => {
    setIsDragging(false);
    onDragEnd(result);
  };

  // Create the context value object
  const contextValue = {
    isDragging,
    isDraggable,
    toggleDraggable,
    handleDragStart,
    handleDragEnd,
  };

  return (
    <DraggingContext.Provider value={contextValue}>
      {typeof children === 'function' ? children(contextValue) : children}
    </DraggingContext.Provider>
  );
};
